import type { EngineEvent, EngineEventType, EventListener, EventUnsubscribe } from './types';

/**
 * EventEmitter - Generic event emitter for engine events
 */
export class EventEmitter {
  private listeners: Map<EngineEventType, Set<EventListener>> = new Map();
  private allListeners: Set<EventListener> = new Set();

  /**
   * Subscribe to a specific event type
   */
  on<T = unknown>(type: EngineEventType, listener: EventListener<T>): EventUnsubscribe {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(listener as EventListener);

    return () => {
      this.listeners.get(type)?.delete(listener as EventListener);
    };
  }

  /**
   * Subscribe to all events
   */
  onAny<T = unknown>(listener: EventListener<T>): EventUnsubscribe {
    this.allListeners.add(listener as EventListener);
    return () => {
      this.allListeners.delete(listener as EventListener);
    };
  }

  /**
   * Subscribe once to a specific event type
   */
  once<T = unknown>(type: EngineEventType, listener: EventListener<T>): EventUnsubscribe {
    const unsubscribe = this.on(type, (event) => {
      unsubscribe();
      (listener as EventListener)(event);
    });
    return unsubscribe;
  }

  /**
   * Emit an event
   */
  emit<T = unknown>(event: EngineEvent<T>): void {
    // Notify specific listeners
    const listeners = this.listeners.get(event.type);
    if (listeners) {
      listeners.forEach((listener) => listener(event));
    }

    // Notify all-event listeners
    this.allListeners.forEach((listener) => listener(event));
  }

  /**
   * Remove all listeners for a specific event type
   */
  off(type: EngineEventType): void {
    this.listeners.delete(type);
  }

  /**
   * Remove all listeners
   */
  removeAllListeners(): void {
    this.listeners.clear();
    this.allListeners.clear();
  }

  /**
   * Get listener count for a specific event type
   */
  listenerCount(type: EngineEventType): number {
    return this.listeners.get(type)?.size ?? 0;
  }
}

/**
 * Create a simple event for quick emission
 */
export function createEvent<T = unknown>(
  type: EngineEventType,
  contentId: string,
  data?: T
): EngineEvent<T> {
  return {
    type,
    contentId,
    timestamp: Date.now(),
    data,
  };
}
